import React, { useState } from 'react';
import { useAuth }         from '../context/AuthContext';
import './ChangePasswordPage.css';

// Defined outside to prevent re-mount on every keystroke
const EyeIcon = ({ visible }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </>
    )}
  </svg>
);

const PasswordField = ({ label, name, placeholder, value, show, onChange, onToggle }) => (
  <div className="input-group">
    <label htmlFor={name}>{label}</label>
    <div className="cp-input-wrap">
      <input
        id={name}
        name={name}
        type={show ? 'text' : 'password'}
        className="input-field cp-input"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={name === 'current' ? 'current-password' : 'new-password'}
        required
      />
      <button
        type="button"
        className="cp-eye-btn"
        onClick={onToggle}
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        <EyeIcon visible={show} />
      </button>
    </div>
  </div>
);

export default function ChangePasswordPage() {
  const { changePassword, logout, user } = useAuth();

  const [form, setForm]       = useState({ current: '', newPass: '', confirm: '' });
  const [show, setShow]       = useState({ current: false, newPass: false, confirm: false });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const toggle = field => setShow(s => ({ ...s, [field]: !s[field] }));
  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (form.newPass.length < 8) {
      return setError('New password must be at least 8 characters.');
    }
    if (form.newPass !== form.confirm) {
      return setError('New password and confirmation do not match.');
    }
    if (form.newPass === form.current) {
      return setError('New password must be different from the current password.');
    }

    setLoading(true);
    try {
      await changePassword(form.current, form.newPass);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-grid" aria-hidden />
      <div className="login-bg-glow" aria-hidden />

      <div className="login-card cp-card fade-up">
        <div className="cp-header">
          <div className="cp-lock-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1 className="cp-title">Set Your Password</h1>
          <p className="cp-subtitle">
            Welcome, <strong>{user?.username}</strong>. You must set a new password before continuing.
          </p>
        </div>

        <div className="login-divider" />

        {error && (
          <div className="login-error fade-up">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <PasswordField
            label="Current (Temporary) Password"
            name="current"
            placeholder="Enter your temporary password"
            value={form.current}
            show={show.current}
            onChange={handle}
            onToggle={() => toggle('current')}
          />
          <PasswordField
            label="New Password"
            name="newPass"
            placeholder="At least 8 characters"
            value={form.newPass}
            show={show.newPass}
            onChange={handle}
            onToggle={() => toggle('newPass')}
          />
          <PasswordField
            label="Confirm New Password"
            name="confirm"
            placeholder="Re-enter new password"
            value={form.confirm}
            show={show.confirm}
            onChange={handle}
            onToggle={() => toggle('confirm')}
          />

          <div className="cp-requirements">
            <span className={form.newPass.length >= 8 ? 'req-met' : 'req-unmet'}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                {form.newPass.length >= 8 ? <polyline points="20 6 9 17 4 12"/> : <line x1="18" y1="6" x2="6" y2="18"/>}
              </svg>
              At least 8 characters
            </span>
            <span className={(form.confirm && form.newPass === form.confirm) ? 'req-met' : 'req-unmet'}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                {(form.confirm && form.newPass === form.confirm) ? <polyline points="20 6 9 17 4 12"/> : <line x1="18" y1="6" x2="6" y2="18"/>}
              </svg>
              Passwords match
            </span>
          </div>

          <button type="submit" className="btn btn-primary w-full" disabled={loading}>
            {loading ? (
              <><span className="btn-spinner" /> Saving…</>
            ) : 'Set New Password & Continue'}
          </button>
        </form>

        <p className="login-footer">
          Not you?{' '}
          <button type="button" className="cp-logout-link" onClick={logout}>
            Sign out
          </button>
        </p>
      </div>
    </div>
  );
}
